import { action, makeObservable, observable } from 'mobx';
import type { StepUpInteractiveHost } from '@wix/stepup';

/**
 * Interactive host for post-login step-up (@wix/stepup StepUpInteractiveHost).
 *
 * The silent prompt=none attempt runs with NO UI. Only when it returns
 * interaction_required does StepUpIframe call open(url, onCancel) to show a visible
 * /authorize iframe; this store holds that modal state and StepUpModal renders
 * from it. StepUpIframe calls close() on EVERY settle (success / failure / cancel), so
 * the modal tears down on its own — the flow never closes it here. The host is purely
 * presentational: it does not receive the code (StepUpIframe's own web_message
 * listener does).
 */
export class StepUpModalStore implements StepUpInteractiveHost {
  isOpen = false;
  url: string | null = null;
  private onCancelCallback: (() => void) | null = null;

  constructor() {
    makeObservable(this, {
      isOpen: observable,
      url: observable,
      open: action.bound,
      close: action.bound,
      requestCancel: action.bound,
    });
  }

  open(url: string, onCancel: () => void): void {
    this.url = url;
    this.onCancelCallback = onCancel;
    this.isOpen = true;
  }

  close(): void {
    this.isOpen = false;
    this.url = null;
    this.onCancelCallback = null;
  }

  // User dismissed the modal (ESC / close button). Reject the step-up as cancelled;
  // StepUpIframe then settles and calls close(), so we do not tear down here.
  requestCancel(): void {
    this.onCancelCallback?.();
  }
}
